import { computed, observable } from 'mobx';
import moment from 'moment';
import type { Moment } from '../lib/moment';

export type RentalCarResponseArgs = {
  departure_time?: string;
  departure_time_type?: number;
  return_time?: string;
  return_time_type?: number;
  departure_place?: string;
  return_place?: 'current' | 'other';
  return_place_name?: string;
  car_type?: string;
  car_type_options?: [string, string][];
  car_type_other?: string;
  car_number?: number | string;
  remarks?: string;
  first_name_kana?: string;
  last_name_kana?: string;
  driver_tel?: string;
  origin?: string;
  origin_address?: string | null;
  destination?: string;
  destination_address?: string | null;
};

export type RentalCarArgs = {
  departureTime: Moment;
  departureTimeType: number;
  returnTime: Moment;
  returnTimeType: number;
  departurePlace: string;
  returnPlace: 'current' | 'other';
  returnPlaceName: string;
  carType: string;
  carTypeOptions: [string, string][];
  carTypeOther: string;
  carNumber: number | string;
  remarks: string;
  firstNameKana: string;
  lastNameKana: string;
  driverTel: string;
};

type ResolveRentalCarDriverTelArgs = {
  driverTel?: string;
  traveler?: {
    user?: {
      tel?: string;
    };
    flightTel?: string;
  };
};

export const resolveRentalCarDriverTel = ({ driverTel, traveler }: ResolveRentalCarDriverTelArgs) =>
  driverTel || traveler?.user?.tel || traveler?.flightTel || '';

export const convertRentalCarResponseToArgs = (
  response: RentalCarResponseArgs,
  fallbackCarTypeOptions: [string, string][] = []
) => {
  const departurePlace =
    response.departure_place ??
    (response.origin_address ? `${response.origin}(${response.origin_address})` : response.origin) ??
    '';
  const returnPlace =
    response.return_place ??
    (response.origin === response.destination || !response.destination ? 'current' : 'other');
  const returnPlaceName =
    response.return_place_name ??
    (returnPlace === 'current'
      ? ''
      : response.destination_address
      ? `${response.destination}(${response.destination_address})`
      : response.destination) ??
    '';
  const args: RentalCarArgs = {
    departureTime: moment(response.departure_time),
    departureTimeType: response.departure_time_type ?? 1,
    returnTime: moment(response.return_time),
    returnTimeType: response.return_time_type ?? 1,
    departurePlace,
    returnPlace,
    returnPlaceName,
    carType: response.car_type || 'compact',
    carTypeOptions: response.car_type_options ?? fallbackCarTypeOptions,
    carTypeOther: response.car_type_other || '',
    carNumber: response.car_number || 1,
    remarks: response.remarks || '',
    firstNameKana: response.first_name_kana || '',
    lastNameKana: response.last_name_kana || '',
    driverTel: response.driver_tel || ''
  };
  return args;
};

export default class RentalCar {
  @observable departureTime: Moment;
  @observable departureTimeType: number;
  @observable returnTime: Moment;
  @observable returnTimeType: number;
  @observable departurePlace: string;
  @observable returnPlace: 'current' | 'other';
  @observable returnPlaceName: string;
  @observable carType: string;
  @observable carTypeOptions: [string, string][];
  @observable carTypeOther: string;
  @observable carNumber: number | string;
  @observable remarks: string;
  @observable firstNameKana: string;
  @observable lastNameKana: string;
  @observable driverTel: string;

  constructor(args: RentalCarArgs) {
    this.departureTime = args.departureTime;
    this.departureTimeType = args.departureTimeType;
    this.returnTime = args.returnTime;
    this.returnTimeType = args.returnTimeType;
    this.departurePlace = args.departurePlace;
    this.returnPlace = args.returnPlace;
    this.returnPlaceName = args.returnPlaceName;
    this.carType = args.carType;
    this.carTypeOptions = args.carTypeOptions;
    this.carTypeOther = args.carTypeOther;
    this.carNumber = args.carNumber;
    this.remarks = args.remarks;
    this.firstNameKana = args.firstNameKana;
    this.lastNameKana = args.lastNameKana;
    this.driverTel = args.driverTel;
  }

  @computed get submitParams() {
    return {
      departure_date: this.departureTime.format('YYYY-MM-DD HH:mm'),
      departure_place: this.departurePlace,
      return_date: this.returnTime.format('YYYY-MM-DD HH:mm'),
      return_place: this.returnPlace === 'current' ? this.departurePlace : this.returnPlaceName,
      car_type: this.carType,
      car_type_other: this.carType === 'other' ? this.carTypeOther : '',
      car_number: this.carNumber,
      remarks: this.remarks,
      first_name_kana: this.firstNameKana,
      last_name_kana: this.lastNameKana,
      driver_tel: this.driverTel
    };
  }
}
